import { createPortal } from "react-dom"
import { useNavigate } from "react-router-dom"
import { ChevronDown, Settings, LogOut, Building2, Check, ShieldCheck } from "lucide-react"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { usePopover } from "@/app/hooks/usePopover"
import { UserAvatar } from "@/app/components/UserAvatar"
import { clientId, csrfToken } from "@/app/api/client"

// Fixed menu width. usePopover left-aligns the popover to the trigger; we render
// at this width and compute `left` from the trigger's RIGHT edge to right-align.
const MENU_WIDTH = 248

/**
 * Header user dropdown: avatar + name trigger opening a themed menu with profile
 * settings, company settings (owners/staff only), and logout. Built on
 * usePopover — the menu portals to <body> and closes on outside-click/scroll.
 */
export function UserMenu() {
  const { theme } = useClientTheme()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { user, canManageUsers } = useCurrentUser()
  const { open, setOpen, triggerRef, popoverRef, popoverStyle } = usePopover<HTMLButtonElement>()

  if (!user) return null

  const go = (path: string) => {
    navigate(path)
    setOpen(false)
  }

  async function handleLogout() {
    try {
      await fetch(`/${clientId}/session`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": csrfToken ?? "" },
        redirect: "manual",
      })
    } finally {
      // Hard-navigate regardless — the server session is the source of truth and
      // the login page re-challenges, so a failed DELETE must not strand the user.
      window.location.href = `/${clientId}/session/new`
    }
  }

  const itemRow = (icon: React.ReactNode, label: string, onClick: () => void) => (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex items-center w-full"
      style={{
        gap: "8px",
        padding: "8px 12px",
        background: "none",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "var(--font-ui)",
        fontSize: "13px",
        color: theme.textPrimary,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.bgSurface2
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent"
      }}
    >
      {icon}
      <span style={{ whiteSpace: "nowrap" }}>{label}</span>
    </button>
  )

  // Like itemRow, but a real <a> for a full page load (client switching and
  // the admin panel live outside this SPA's router — a client-side
  // navigate() wouldn't re-bootstrap the theme/data for the new context).
  const linkRow = (
    icon: React.ReactNode,
    label: string,
    href: string,
    trailing?: React.ReactNode
  ) => (
    <a
      href={href}
      role="menuitem"
      className="flex items-center w-full"
      style={{
        gap: "8px",
        padding: "8px 12px",
        textDecoration: "none",
        fontFamily: "var(--font-ui)",
        fontSize: "13px",
        color: theme.textPrimary,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = theme.bgSurface2
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent"
      }}
    >
      {icon}
      <span
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          flex: 1,
        }}
      >
        {label}
      </span>
      {trailing}
    </a>
  )

  const sectionLabel = (label: string) => (
    <div
      style={{
        padding: "8px 12px 2px",
        fontSize: "11px",
        fontWeight: 600,
        color: theme.textMuted,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </div>
  )

  const clientList = user.clients ?? []
  const showSwitcher = clientList.length > 1
  const triggerRect = triggerRef.current?.getBoundingClientRect()

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
        className="flex items-center"
        style={{
          gap: "8px",
          // A padded, rounded hit-area with a subtle hover/open background so the
          // trigger reads as clickable. Negative right margin keeps the avatar
          // visually aligned to the header edge despite the added padding.
          padding: "4px 8px",
          marginRight: "-4px",
          borderRadius: "8px",
          border: "none",
          cursor: "pointer",
          backgroundColor: open ? theme.bgSurface2 : "transparent",
          transition: "background-color 120ms ease",
        }}
        onMouseEnter={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = theme.bgSurface2
        }}
        onMouseLeave={(e) => {
          if (!open) e.currentTarget.style.backgroundColor = "transparent"
        }}
      >
        <UserAvatar name={user.name} avatarUrl={user.avatar_url} size={28} />
        {!isMobile && (
          <span
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: "13px",
              fontWeight: 500,
              color: theme.textPrimary,
              whiteSpace: "nowrap",
            }}
          >
            {user.name}
          </span>
        )}
        <ChevronDown size={14} style={{ color: theme.textMuted, flexShrink: 0 }} />
      </button>

      {open &&
        popoverStyle &&
        createPortal(
          <div
            ref={popoverRef}
            role="menu"
            className="rounded-lg overflow-hidden"
            style={{
              ...popoverStyle,
              width: MENU_WIDTH,
              minWidth: MENU_WIDTH,
              left: Math.max(8, (triggerRect?.right ?? 0) - MENU_WIDTH),
              // usePopover caps maxHeight at 280px — too short for the fuller
              // superadmin menu (switcher + Admin Panel + Log Out), which clipped
              // the bottom items with no way to reach them. Use the real space
              // below the trigger and let the whole menu scroll only if it still
              // overflows the viewport.
              maxHeight: triggerRect
                ? Math.max(240, window.innerHeight - triggerRect.bottom - 12)
                : popoverStyle.maxHeight,
              overflowY: "auto",
              zIndex: 60,
              backgroundColor: theme.bgSurface,
              boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            }}
          >
            {/* Header block — non-interactive identity summary. */}
            <div
              className="flex items-center"
              style={{
                gap: "10px",
                padding: "12px",
                borderBottom: `1px solid ${theme.borderColor}`,
              }}
            >
              <UserAvatar name={user.name} avatarUrl={user.avatar_url} size={48} />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: theme.textPrimary,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user.name}
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-ui)",
                    fontSize: "12px",
                    color: theme.textMuted,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {user.email_address}
                </div>
              </div>
            </div>

            {itemRow(
              <Settings size={15} style={{ color: theme.textMuted, flexShrink: 0 }} />,
              "Profile Settings",
              () => go("/settings/profile")
            )}
            {canManageUsers &&
              itemRow(
                <Building2 size={15} style={{ color: theme.textMuted, flexShrink: 0 }} />,
                "Company Settings",
                () => go("/settings/company")
              )}

            {showSwitcher && (
              <>
                <div style={{ borderTop: `1px solid ${theme.borderColor}` }} />
                {sectionLabel("Switch client")}
                {/* Bounded + scrollable so a superadmin with many clients can't
                    push Admin Panel / Log Out out of reach. */}
                <div style={{ maxHeight: 200, overflowY: "auto" }}>
                  {clientList.map((c) =>
                    linkRow(
                      <span
                        style={{
                          width: 15,
                          display: "inline-flex",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      />,
                      c.name,
                      `/${c.slug}/`,
                      c.slug === clientId ? (
                        <Check size={14} style={{ color: theme.textMuted, flexShrink: 0 }} />
                      ) : null
                    )
                  )}
                </div>
              </>
            )}

            {user.superadmin && (
              <>
                <div style={{ borderTop: `1px solid ${theme.borderColor}` }} />
                {linkRow(
                  <ShieldCheck size={15} style={{ color: theme.textMuted, flexShrink: 0 }} />,
                  "Admin Panel",
                  "/admin"
                )}
              </>
            )}

            <div style={{ borderTop: `1px solid ${theme.borderColor}` }} />

            {itemRow(
              <LogOut size={15} style={{ color: theme.textMuted, flexShrink: 0 }} />,
              "Log Out",
              handleLogout
            )}
          </div>,
          document.body
        )}
    </>
  )
}
